export const MAX_VIDEO_BYTES = 25 * 1024 * 1024;
export const MAX_AUDIO_SECONDS = 120;

export interface ImageCropPosition { x: number; y: number }

const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function imageCropSourceRect(width: number, height: number, position: ImageCropPosition) {
  const side = Math.min(width, height);
  return { x: (width - side) * clamp(position.x), y: (height - side) * clamp(position.y), side };
}

export function moveImageCrop(width: number, height: number, frameSize: number, position: ImageCropPosition, deltaX: number, deltaY: number): ImageCropPosition {
  const scale = frameSize / Math.min(width, height);
  const overflowX = width * scale - frameSize;
  const overflowY = height * scale - frameSize;
  return {
    x: overflowX > 0 ? clamp(position.x - deltaX / overflowX) : position.x,
    y: overflowY > 0 ? clamp(position.y - deltaY / overflowY) : position.y
  };
}

export async function videoThumbnail(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;
  try {
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0 || video.duration > 120) { reject(new Error("Video must be 2 minutes or shorter")); return; }
        video.currentTime = Math.min(0.25, video.duration / 2);
      };
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error("This browser could not preview the video. Choose an MP4 file it can play."));
    });
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error("Could not create the video thumbnail")), "image/jpeg", 0.86));
    return new File([blob], "video-thumbnail.jpg", { type: "image/jpeg" });
  } finally {
    video.removeAttribute("src"); video.load(); URL.revokeObjectURL(url);
  }
}

export async function audioToWav(blob: Blob): Promise<{ file: File; durationInSeconds: number }> {
  const context = new AudioContext();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    if (!Number.isFinite(decoded.duration) || decoded.duration <= 0 || decoded.duration > MAX_AUDIO_SECONDS) throw new Error("Audio must be 2 minutes or shorter");
    const sampleRate = 16_000;
    const sampleCount = Math.ceil(decoded.duration * sampleRate);
    const output = new ArrayBuffer(44 + sampleCount * 2);
    const view = new DataView(output);
    const write = (offset: number, value: string) => { for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i)); };
    write(0, "RIFF"); view.setUint32(4, output.byteLength - 8, true); write(8, "WAVE");
    write(12, "fmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    write(36, "data"); view.setUint32(40, sampleCount * 2, true);
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, index) => decoded.getChannelData(index));
    for (let index = 0; index < sampleCount; index++) {
      const source = Math.min(decoded.length - 1, Math.floor(index * decoded.sampleRate / sampleRate));
      const mono = channels.reduce((sum, channel) => sum + channel[source], 0) / channels.length;
      const value = Math.max(-1, Math.min(1, mono));
      view.setInt16(44 + index * 2, Math.round(value < 0 ? value * 32768 : value * 32767), true);
    }
    return { file: new File([output], "template-audio.wav", { type: "audio/wav" }), durationInSeconds: Math.max(1, Math.ceil(decoded.duration)) };
  } catch (error) {
    if (error instanceof DOMException) throw new Error("This browser could not read that audio file. Try MP3, M4A, or WAV.");
    throw error;
  } finally { await context.close(); }
}
